import { BrandTheme } from './portal';

/**
 * Device-remembered program brand. The program a participant belongs to is only
 * resolvable AFTER auth (via /me/context → /brands/:id/theme), but the pre-auth
 * screens — sign-in, forgot-password, onboarding — should still wear that brand,
 * the same way the logged-in header does. We bridge the gap by caching the last
 * resolved brand on the device after a successful sign-in and re-applying it on
 * the pre-auth screens. A device that has never signed in falls back to neutral.
 */
const LAST_BRAND_KEY = 'aab_last_brand';

/** The minimal brand identity the pre-auth screens need to render. */
export interface RememberedBrand {
  slug?: string;
  name?: string;
  logoUrl?: string | null;
  colors?: Record<string, string> | null;
  portalTheme?: Record<string, string> | null;
}

/** Globus brand colors → the design system's CSS custom properties. Only
 *  resolved values are applied; absent ones leave the neutral defaults. Shared
 *  by the logged-in context and the pre-auth screens so both re-skin identically.
 *
 *  --brand-50 is skipped in dark mode: Globus supplies it as a *pale* wash
 *  sized for a white card, and an inline style here would always beat the
 *  stylesheet's dark-scheme override for the same property on the same
 *  element (document.documentElement) — so the dark-appropriate brand tint
 *  computed in styles.css ([data-theme='dark'] --brand-50: color-mix(...))
 *  can only take effect if we don't set it ourselves while dark.
 *
 *  portalTheme keys (topNavBg, topNavText, sidebarButtonSelectedBg, etc.) are
 *  injected verbatim as --{key} so the shell CSS classes can consume them with
 *  var(--topNavBg, <fallback>) without any component-level style props. */
export function applyBrandTheme(theme?: { colors?: Record<string, string> | null; portalTheme?: Record<string, string> | null } | null) {
  const root = document.documentElement;
  const isDark = root.getAttribute('data-theme') === 'dark'
    || (root.getAttribute('data-theme') !== 'light' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const colors = theme?.colors ?? undefined;
  const vars: Record<string, string | undefined> = {
    '--brand': colors?.primary,
    '--brand-600': colors?.buttonBackground,
    '--brand-50': isDark ? undefined : colors?.secondary,
    '--accent': colors?.accent,
  };
  for (const [key, value] of Object.entries(vars)) {
    if (value) root.style.setProperty(key, value);
    else root.style.removeProperty(key);
  }

  // portalTheme: inject each key as --{key} so shell CSS classes consume them
  // via var(--topNavBg, <fallback>). Keys with no value are removed so the
  // stylesheet fallback wins (brand leaves a surface unstyled → neutral default).
  const portalTheme = theme?.portalTheme ?? undefined;
  const portalKeys = [
    'topNavBg', 'topNavText',
    'sidebarButtonSelectedBg', 'sidebarButtonHoverBg', 'sidebarButtonText',
    'footerBg', 'footerText',
    'profileContainerBg', 'profileContainerBorder', 'profilePhotoBorder',
  ];
  for (const key of portalKeys) {
    const value = portalTheme?.[key];
    if (value) root.style.setProperty(`--${key}`, value);
    else root.style.removeProperty(`--${key}`);
  }
}

/** Persist the participant's resolved brand for the pre-auth screens. A brand
 *  with neither a logo nor colors carries no visual identity, so we skip it
 *  rather than overwrite a good cached brand with an empty one. */
export function rememberBrand(brand?: { slug?: string; name?: string } | null, theme?: BrandTheme) {
  const remembered: RememberedBrand = {
    slug: brand?.slug ?? theme?.slug,
    name: brand?.name ?? theme?.name,
    logoUrl: theme?.logoUrl ?? null,
    colors: theme?.colors ?? null,
    portalTheme: theme?.portalTheme ?? null,
  };
  if (!remembered.logoUrl && !remembered.colors && !remembered.portalTheme) return;
  try {
    localStorage.setItem(LAST_BRAND_KEY, JSON.stringify(remembered));
  } catch {
    /* storage disabled / full — pre-auth branding just falls back to neutral */
  }
}

/**
 * Gate a brand `logoUrl` before it is rendered as an <img src>. The value comes
 * from the Globus brand record (BFF-absolutized) and is cached in localStorage,
 * so a spoofed/tampered brand record could otherwise point the logo at an
 * arbitrary external URL (tracking beacon). Allow only same-origin relative
 * paths and https Alliance Abroad hosts; anything else falls back to the
 * neutral badge. Not an XSS sink today (img src can't execute), so this is a
 * trust/exfiltration control, not a script-injection fix.
 */
const LOGO_ALLOWED_HOST_SUFFIX = '.allianceabroad.com';
export function safeBrandLogoUrl(url?: string | null): string | undefined {
  if (!url) return undefined;
  if (url.startsWith('/')) return url; // same-origin relative
  try {
    const u = new URL(url);
    if (
      u.protocol === 'https:' &&
      (u.hostname === 'allianceabroad.com' || u.hostname.endsWith(LOGO_ALLOWED_HOST_SUFFIX))
    ) {
      return url;
    }
  } catch {
    /* not a parseable absolute URL */
  }
  return undefined;
}

/** The last brand resolved on this device, if any. */
export function getRememberedBrand(): RememberedBrand | undefined {
  try {
    const raw = localStorage.getItem(LAST_BRAND_KEY);
    return raw ? (JSON.parse(raw) as RememberedBrand) : undefined;
  } catch {
    return undefined;
  }
}
